import type { PlaybackSnapshot } from '../types';

const DRIFT_RESYNC_MS = 150;

export class PlaybackClock {
  private trackId: string | null = null;
  private anchorProgressMs = 0;
  private anchorPerfMs = 0;
  private isPlaying = false;
  private durationMs = 0;

  sync(snapshot: PlaybackSnapshot): void {
    const estimated = this.getProgressMs();

    if (snapshot.trackId !== this.trackId) {
      this.trackId = snapshot.trackId;
      this.anchorProgressMs = snapshot.progressMs;
      this.anchorPerfMs = performance.now();
      this.isPlaying = snapshot.isPlaying;
      this.durationMs = snapshot.durationMs;
      return;
    }

    const drift = Math.abs(estimated - snapshot.progressMs);
    if (!this.isPlaying && snapshot.isPlaying) {
      this.anchorProgressMs = snapshot.progressMs;
      this.anchorPerfMs = performance.now();
    } else if (this.isPlaying && !snapshot.isPlaying) {
      this.anchorProgressMs = snapshot.progressMs;
      this.anchorPerfMs = performance.now();
    } else if (drift > DRIFT_RESYNC_MS) {
      this.anchorProgressMs = snapshot.progressMs;
      this.anchorPerfMs = performance.now();
    }

    this.isPlaying = snapshot.isPlaying;
    this.durationMs = snapshot.durationMs;
  }

  getProgressMs(): number {
    if (!this.isPlaying) return this.anchorProgressMs;
    const elapsed = performance.now() - this.anchorPerfMs;
    return Math.min(this.durationMs, Math.max(0, this.anchorProgressMs + elapsed));
  }

  getTrackId(): string | null {
    return this.trackId;
  }

  getIsPlaying(): boolean {
    return this.isPlaying;
  }
}

export function normalizeSpotifyPlayback(data: Record<string, unknown> | null): PlaybackSnapshot | null {
  if (!data?.item) return null;
  const item = data.item as Record<string, unknown>;
  const artists = item.artists as { name: string }[] | undefined;
  const album = item.album as { name?: string; images?: { url: string }[] } | undefined;
  const device = data.device as { name?: string; type?: string } | undefined;

  const durationMs = Number(item.duration_ms ?? 0);
  const rawProgressMs = Number(data.progress_ms ?? 0);
  const timestamp = Number(data.timestamp ?? Date.now());
  const responseAgeMs = Boolean(data.is_playing)
    ? Math.max(0, Math.min(4000, Date.now() - timestamp))
    : 0;

  return {
    trackId: String(item.id ?? ''),
    title: String(item.name ?? ''),
    artist: artists?.map((a) => a.name).join(', ') ?? '',
    album: album?.name ?? '',
    albumArtUrl: album?.images?.[0]?.url ?? '',
    durationMs,
    progressMs: Math.min(durationMs, Math.max(0, rawProgressMs + responseAgeMs)),
    isPlaying: Boolean(data.is_playing),
    timestamp,
    deviceName: device?.name ?? '',
    deviceType: device?.type ?? '',
  };
}

export function normalizeQueueItem(item: Record<string, unknown>) {
  const artists = item.artists as { name: string }[] | undefined;
  const album = item.album as { images?: { url: string }[] } | undefined;
  return {
    id: String(item.id ?? ''),
    title: String(item.name ?? ''),
    artist: artists?.map((a) => a.name).join(', ') ?? '',
    albumArtUrl: album?.images?.[0]?.url ?? '',
    durationMs: Number(item.duration_ms ?? 0),
  };
}
